import { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { siteboardAction, setAlert } from '../actions/defaultActions';
import { SET_FEEDBACK_DIALOGUE_OPEN, SUBMIT_FEEDBACK } from '../actions/types';

import PopupWindow from './PopupWindow';
import PushButton from './PushButton';

import {
  MessageSquareTextIcon,
  SendIcon,
  CheckIcon,
  SparklesIcon,
  HeartIcon,
  ArrowRightIcon,
} from 'lucide-react';

const FeedbackDialogue = ({ _users, _userId, _path, siteboardAction, setAlert }) => {
  const [inputText, setInputText] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const closeDialogue = () => {
    siteboardAction(SET_FEEDBACK_DIALOGUE_OPEN, { isOpen: false });
  };

  const onSubmit = () => {
    try {
      if(inputText.length === 0) throw new Error('Please enter your feedback');
      siteboardAction(SUBMIT_FEEDBACK, {
        feedbackText: inputText,
        path: _path,
      }, () => {
        setHasSubmitted(true);
      });
    } catch(error) {
      setAlert(''+error.message);
    }
  };

  // Helper keys to switch screens for testing
  useEffect(() => {
    if(process.env.REACT_APP_LIVE_MODE === 'true') return;

    const onKeyUp = (event) => {
      if(event.key === 'ArrowLeft') { 
        setHasSubmitted(false);
      } else if(event.key === 'ArrowRight') { 
        setHasSubmitted(true);
      }
    };

    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keyup', onKeyUp); }; // remove listener when component unloads
  }, []);

  return (
    <PopupWindow
      title="Feedback"
      closeDialogue={closeDialogue}
      bgStyle="bg-neutral-800"
    >
      <div className="flex w-full max-w-screen-md flex-col text-white">

        {!hasSubmitted ? (
          <div className="flex flex-col gap-5">

            {/* Header */}
            <div className="flex items-start gap-4">

              <div className="
                flex h-11 w-11 shrink-0 items-center justify-center
                rounded-xl border border-[#8060ff]/30
                bg-[#471cff]/15 text-[#b4a0ff]
              ">
                <MessageSquareTextIcon
                  size={22}
                  strokeWidth={1.8}
                />
              </div>

              <div className="flex min-w-0 flex-col">
                <h2 className="
                  text-xl font-bold tracking-tight text-white
                ">
                  We'd love your feedback
                </h2>

                <p className="text-sm leading-relaxed text-[#c4c4cc]">
                  Your suggestions help us make the experience better
                  for everyone.
                </p>
              </div>
            </div>

            {/* Feedback field */}
            <div className="flex flex-col gap-1">

              <label
                htmlFor="feedback-message"
                className="text-sm font-semibold text-white/90"
              >
                What could we improve?
              </label>

              <p className="leading-relaxed text-[#aaaab5]">
                Tell us what you'd change, what's missing, or what
                would make this page more useful.
              </p>

              <textarea
                id="feedback-message"
                value={inputText}
                onChange={(event) => {
                  setInputText(event.target.value);
                }}
                placeholder="Share your thoughts or ideas..."
                rows={6}
                className="project-detail-input min-h-[150px] resize-y"
              />
            </div>

            {/* Footer */}
            <div className="flex flex-col gap-3">

              <PushButton
                textColor="white"
                bgColor="#471cff"
                hoverBgColor="#603aff"
                onClick={onSubmit}
              >
                <div className="
                  group flex w-full items-center justify-center
                  gap-2 py-1 font-semibold
                ">
                  <SendIcon
                    size={18}
                    strokeWidth={2}
                  />
                  <span>Submit Feedback</span>
                </div>
              </PushButton>

              <p className="
                text-center text-sm leading-relaxed text-[#9999a5]
              ">
                We read every submission and appreciate your input.
              </p>
            </div>
          </div>
        ) : (

          /* Success screen */
          <div className="
            flex flex-col items-center
            px-2 py-5 text-center sm:py-7
          ">

            {/* Animated celebration */}
            <div className="
              feedback-success-celebration
              relative mb-7 flex h-32 w-32
              items-center justify-center
            ">

              {/* Soft circular background */}
              <div className="
                feedback-success-halo
                absolute inset-3 rounded-full
                bg-emerald-500/10
              " />

              {/* Outer circle */}
              <div className="
                feedback-success-ring
                absolute inset-4 rounded-full
                border-2 border-emerald-400/25
              " />

              {/* Confetti particles */}
              <span className="feedback-confetti feedback-confetti-1" />
              <span className="feedback-confetti feedback-confetti-2" />
              <span className="feedback-confetti feedback-confetti-3" />
              <span className="feedback-confetti feedback-confetti-4" />
              <span className="feedback-confetti feedback-confetti-5" />
              <span className="feedback-confetti feedback-confetti-6" />
              <span className="feedback-confetti feedback-confetti-7" />
              <span className="feedback-confetti feedback-confetti-8" />

              {/* Success circle */}
              <div className="
                feedback-success-circle
                relative z-10 flex h-[76px] w-[76px]
                items-center justify-center
                rounded-full border border-emerald-400/40
                bg-[#123a30]
                shadow-[0_0_35px_rgba(16,185,129,0.2)]
              ">
                <CheckIcon
                  size={40}
                  strokeWidth={2.7}
                  className="
                    feedback-success-check text-emerald-400
                  "
                />
              </div>

              {/* Decorative sparkles */}
              <SparklesIcon
                size={17}
                className="
                  feedback-success-sparkle
                  absolute right-0 top-3 text-[#f9a928]
                "
              />

              <SparklesIcon
                size={13}
                className="
                  feedback-success-sparkle
                  absolute bottom-4 left-1 text-[#b4a0ff]
                  [animation-delay:0.2s]
                "
              />
            </div>

            {/* Success text */}
            <div className="feedback-success-content flex flex-col items-center">

              <div className="
                mb-3 inline-flex items-center gap-1.5
                rounded-full border border-emerald-400/25
                bg-emerald-400/10 px-3 py-1
                text-xs font-semibold text-emerald-400
              ">
                <CheckIcon size={13} strokeWidth={2.5} />
                Feedback submitted
              </div>

              <h2 className="
                mb-3 text-2xl font-bold tracking-tight
                text-white sm:text-[27px]
              ">
                Thanks for your feedback!
              </h2>

              <p className="
                max-w-[380px] text-sm leading-relaxed
                text-[#c4c4cc]
              ">
                We really appreciate you taking the time to
                share your thoughts with us.
              </p>

              <p className="
                mt-3 max-w-[380px] text-sm leading-relaxed
                text-[#a7a7b0]
              ">
                We carefully review every submission. If your message
                needs a response, we'll do our best to get back
                to you as soon as possible.
              </p>
            </div>

            {/* Close button */}
            <div className="mt-7 w-full max-w-[240px]">
              <PushButton
                textColor="white"
                bgColor="#471cff"
                hoverBgColor="#603aff"
                onClick={closeDialogue}
              >
                <div className="
                  flex items-center justify-center
                  gap-2 py-1 font-semibold
                ">
                  <HeartIcon size={17} strokeWidth={2} />
                  <span>Done</span>
                </div>
              </PushButton>
            </div>

          </div>
        )}
      </div>
    </PopupWindow>
 );
};

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _path: state.localPersistent.path,
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
}};
export default connect(mapStateToProps, { siteboardAction, setAlert })(FeedbackDialogue);
